import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { contenidoAPI, hubError } from '../lib/api';
import { TopBar } from '../components/layout/TopBar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Sparkles, Loader2, Trash2, Edit, Upload, CheckCircle2, AlertTriangle, Info } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABEL = {
  pendiente_revision: 'Por revisar',
  publicando: 'Publicando…',
  publicado: 'Publicado',
};

export default function ContenidoPage() {
  const { setMobileMenuOpen, churchInfo } = useOutletContext();
  const [items, setItems] = useState([]);
  const [segments, setSegments] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [accessError, setAccessError] = useState(null);
  const [segmentFilter, setSegmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('pendiente_revision');
  const [generateFor, setGenerateFor] = useState('devocionales');
  const [topic, setTopic] = useState('');
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', body: '' });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [confirmPublish, setConfirmPublish] = useState(null);
  const [publishing, setPublishing] = useState(null);

  const fetchItems = useCallback(() => {
    setLoading(true);
    contenidoAPI.list({
      segment: segmentFilter === 'all' ? undefined : segmentFilter,
      status: statusFilter === 'all' ? undefined : statusFilter,
    })
      .then(res => {
        setAccessError(null);
        setItems(res.data?.data || []);
        setSegments(res.data?.segments || []);
        setMeta({ model: res.data?.model, bible: res.data?.bible_version,
                  canPublish: res.data?.can_publish, sessionChurch: res.data?.session_church });
      })
      .catch(err => setAccessError(hubError(err, 'No se pudo cargar el contenido')))
      .finally(() => setLoading(false));
  }, [segmentFilter, statusFilter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await contenidoAPI.generate({ segment: generateFor, topic: topic.trim() || undefined });
      if (res.data?.queued) {
        toast.success(res.data.message || 'Generando en segundo plano…');
      } else {
        toast.success(`${res.data?.total || 0} borrador(es) listos para revisar`);
      }
      setTopic('');
      setStatusFilter('pendiente_revision');
      fetchItems();
    } catch (err) { toast.error(hubError(err, 'No se pudo generar el contenido')); }
    finally { setGenerating(false); }
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ title: item.title, body: item.body });
  };

  const save = async () => {
    setSaving(true);
    try {
      await contenidoAPI.update(editing.id, { title: form.title, body: form.body });
      toast.success('Borrador actualizado');
      setEditing(null);
      fetchItems();
    } catch (err) { toast.error(hubError(err, 'No se pudo guardar')); }
    finally { setSaving(false); }
  };

  const publish = async (item) => {
    setConfirmPublish(null);
    setPublishing(item.id);
    try {
      await contenidoAPI.publish(item.id);
      toast.success('Publicado en el feed de la comunidad');
      fetchItems();
    } catch (err) { toast.error(hubError(err, 'No se pudo publicar')); }
    finally { setPublishing(null); }
  };

  const confirmDelete = async () => {
    try {
      await contenidoAPI.remove(deleting.id);
      toast.success('Borrador descartado');
      setDeleting(null);
      fetchItems();
    } catch (err) { toast.error(hubError(err, 'No se pudo eliminar')); }
  };

  const segmentLabel = (key) => segments.find(s => s.key === key)?.label || key;

  return (
    <div className="min-h-screen bg-background" data-testid="contenido-page">
      <TopBar title="Agente de Contenido" subtitle="Feed diario de Comunidad Tu Iglesia"
        churchInfo={churchInfo} onMenuClick={() => setMobileMenuOpen(true)} />

      <div className="p-4 lg:p-8 max-w-5xl">
        {accessError ? (
          <Card className="border-red-200" data-testid="contenido-access-error">
            <CardContent className="p-6 text-center">
              <Info className="w-5 h-5 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-red-700">{accessError}</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className="mb-6 border-[#056A84]/20">
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-4">
                  <Sparkles className="w-4 h-4 text-[#056A84] mt-1 shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    Cada día a las 5:00 a.m. (hora de Venezuela) se generan los borradores del feed.
                    Nada se publica sin tu aprobación. Citas bíblicas: {meta.bible || 'Reina Valera 1909'}.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Select value={generateFor} onValueChange={setGenerateFor}>
                    <SelectTrigger className="sm:w-60" data-testid="contenido-generate-segment">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {segments.map(s => (
                        <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input placeholder="Tema del día (opcional)" value={topic}
                    onChange={(e) => setTopic(e.target.value)} data-testid="contenido-topic" />
                  <Button className="bg-[#056A84] hover:bg-[#045A70] text-white shrink-0"
                    onClick={generate} disabled={generating} data-testid="contenido-generate">
                    {generating
                      ? <><Loader2 className="w-4 h-4 spinner mr-1" /> Generando…</>
                      : <><Sparkles className="w-4 h-4 mr-1" /> Generar ahora</>}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {meta.canPublish === false && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 mb-4"
                data-testid="contenido-publish-blocked">
                <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-800">
                  Tu sesión pertenece a {meta.sessionChurch || 'otra congregación'}, así que el Hub
                  publicaría en ese feed. Para publicar en Comunidad Tu Iglesia hace falta una cuenta
                  de esa congregación.
                </p>
              </div>
            )}

            <div className="flex flex-wrap gap-3 mb-4">
              <Select value={segmentFilter} onValueChange={setSegmentFilter}>
                <SelectTrigger className="w-full sm:w-56" data-testid="contenido-filter-segment">
                  <SelectValue placeholder="Todos los segmentos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los segmentos</SelectItem>
                  {segments.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="contenido-filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pendiente_revision">Por revisar</SelectItem>
                  <SelectItem value="publicado">Publicados</SelectItem>
                  <SelectItem value="all">Todos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 spinner text-[#056A84]" /></div>
            ) : items.length === 0 ? (
              <Card><CardContent className="p-10 text-center text-sm text-muted-foreground"
                data-testid="contenido-empty">
                No hay contenido con estos filtros. Genera uno con el botón de arriba.
              </CardContent></Card>
            ) : (
              <div className="space-y-3">
                {items.map(item => (
                  <Card key={item.id} data-testid={`contenido-item-${item.id}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <Badge variant="secondary" className="text-[10px]">{segmentLabel(item.segment)}</Badge>
                        <Badge className={`text-[10px] ${item.status === 'publicado'
                          ? 'bg-green-100 text-green-700 hover:bg-green-100'
                          : 'bg-amber-100 text-amber-800 hover:bg-amber-100'}`}>
                          {STATUS_LABEL[item.status] || item.status}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{item.content_date}</span>
                        {item.needs_bible_check && (
                          <Badge className="text-[10px] bg-red-100 text-red-700 hover:bg-red-100"
                            data-testid={`contenido-bible-warning-${item.id}`}>
                            <AlertTriangle className="w-3 h-3 mr-1" /> Verificar cita bíblica
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-medium">{item.title}</h3>
                      <p className="text-sm text-muted-foreground whitespace-pre-line mt-2">
                        {item.body.replace(/\*\*/g, '').slice(0, 420)}
                        {item.body.length > 420 ? '…' : ''}
                      </p>
                      <div className="flex items-center gap-2 mt-3">
                        {item.status === 'publicado' ? (
                          <span className="flex items-center text-xs text-green-700">
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> En el feed
                          </span>
                        ) : (
                          <>
                            <Button size="sm" variant="outline" onClick={() => openEdit(item)}
                              className="flex-1 sm:flex-none"
                              data-testid={`contenido-edit-${item.id}`}>
                              <Edit className="w-3.5 h-3.5 mr-1" /> Revisar
                            </Button>
                            <Button size="sm" className="bg-[#056A84] hover:bg-[#045A70] text-white flex-1 sm:flex-none"
                              disabled={publishing === item.id || meta.canPublish === false}
                              title={meta.canPublish === false
                                ? 'Necesitas una cuenta de Comunidad Tu Iglesia para publicar' : undefined}
                              onClick={() => setConfirmPublish(item)}
                              data-testid={`contenido-publish-${item.id}`}>
                              {publishing === item.id
                                ? <Loader2 className="w-3.5 h-3.5 spinner" />
                                : <><Upload className="w-3.5 h-3.5 mr-1" /> Aprobar y publicar</>}
                            </Button>
                            <Button size="sm" variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 shrink-0"
                              onClick={() => setDeleting(item)} data-testid={`contenido-delete-${item.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="sm:max-w-2xl" data-testid="contenido-edit-dialog">
          <DialogHeader>
            <DialogTitle>Revisar antes de publicar</DialogTitle>
            <DialogDescription>
              Ajusta lo que haga falta. El texto se publica tal cual lo dejes aquí.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Título</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="contenido-form-title" />
            </div>
            <div className="space-y-2">
              <Label>Contenido</Label>
              <Textarea rows={16} className="font-mono text-xs" value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                data-testid="contenido-form-body" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button className="bg-[#056A84] hover:bg-[#045A70] text-white" onClick={save}
              disabled={saving} data-testid="contenido-form-save">
              {saving ? <Loader2 className="w-4 h-4 spinner mr-1" /> : null} Guardar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmPublish} onOpenChange={(o) => { if (!o) setConfirmPublish(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="contenido-publish-dialog">
          <DialogHeader>
            <DialogTitle>Publicar en el feed</DialogTitle>
            <DialogDescription>
              «{confirmPublish?.title}» quedará visible para los miembros de Comunidad Tu Iglesia.
              {confirmPublish?.needs_bible_check && ' Ojo: la cita bíblica está marcada como pendiente de verificación.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmPublish(null)}
              data-testid="contenido-publish-cancel">Cancelar</Button>
            <Button className="bg-[#056A84] hover:bg-[#045A70] text-white"
              onClick={() => publish(confirmPublish)} data-testid="contenido-publish-confirm">
              <Upload className="w-4 h-4 mr-1" /> Publicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(o) => { if (!o) setDeleting(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="contenido-delete-dialog">
          <DialogHeader>
            <DialogTitle>Descartar borrador</DialogTitle>
            <DialogDescription>
              Se eliminará «{deleting?.title}». No afecta al feed porque todavía no está publicado.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={confirmDelete}
              data-testid="contenido-delete-confirm">Descartar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
